import { useEffect, useRef } from 'react';
import { useJackpotControllerGetLatestDrawnJackpotsByUserIdQuery } from '@app/store';
import { getLastJackpotCheckAt, setLastJackpotCheckAt } from './helpers';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const POLLING_INTERVAL_MINUTES = 5;

type Jackpot = {
  jackpotSlug: string;
  winAmount: string;
};

export const useJackpotPolling = (onUpdate: (jackpots: Jackpot[]) => void) => {
  const { refetch, isUninitialized } =
    useJackpotControllerGetLatestDrawnJackpotsByUserIdQuery(
      { intervalMinutes: POLLING_INTERVAL_MINUTES },
      { skip: false },
    );

  const initialized = useRef(false);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);

  const fetchJackpots = async () => {
    if (isUninitialized) return;

    try {
      const result = await refetch();
      const jackpots = result.data?.jackpots ?? [];

      if (jackpots.length > 0) {
        onUpdate(jackpots);
      }

      setLastJackpotCheckAt(Date.now());
    } catch (err) {
      console.error('[JackpotPolling] ❌ Ошибка при получении джекпотов:', err);
    }
  };

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const now = Date.now();
    const lastCheck = getLastJackpotCheckAt();
    const elapsed = now - lastCheck;

    const startIntervalPolling = () => {
      fetchJackpots();

      intervalId.current = setInterval(() => {
        fetchJackpots();
      }, CHECK_INTERVAL_MS);
    };

    if (elapsed >= CHECK_INTERVAL_MS) {
      startIntervalPolling();
    } else {
      const delay = CHECK_INTERVAL_MS - elapsed;

      timeoutId.current = setTimeout(() => {
        startIntervalPolling();
      }, delay);
    }

    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
      if (timeoutId.current) clearTimeout(timeoutId.current);
    };
  }, [isUninitialized]);
};
